import { Clock, Brain, Sparkles, Shield, Bookmark, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"

export function HowItHelpsYou() {
  const benefits = [
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Save 15+ Hours Monthly",
      description: "Stop searching through complex regulations. Get immediate answers to your compliance questions."
    },
    {
      icon: <FileText className="h-8 w-8" />,
      title: "Minnesota-Specific",
      description: "All guidance is based specifically on Minnesota statutes and official government documentation."
    },
    {
      icon: <Bookmark className="h-8 w-8" />,
      title: "Exact Citations",
      description: "Every answer includes precise citations to the relevant Minnesota regulatory documents."
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Audit Protection",
      description: "Build confidence with documented compliance decisions backed by official sources."
    }
  ]

  return (
    <section className="relative py-24 md:py-32 bg-white dark:bg-[#0F172A] overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-primary-light/5 dark:bg-primary-light/10 rounded-full blur-3xl opacity-60"></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-gray-500/[0.03] dark:bg-grid-gray-400/[0.05] bg-[length:30px_30px]"></div>
      </div>

      <div className="container px-4 md:px-8 mx-auto relative z-10">
        <div className="text-center mx-auto mb-20">
          <div className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light mb-4 backdrop-blur-sm">
            HOW IT HELPS YOU
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground dark:text-white mb-6 tracking-tight">
            Save Time, Reduce <span className="text-primary dark:text-primary-light">Stress</span>
          </h2>
          <p className="text-xl text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto">
            Get immediate access to the exact Minnesota regulations you need, exactly when you need them.
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left side - Feature cards grid */}
            <div className="lg:col-span-5 space-y-8">
              {/* Key benefits highlight card */}
              <div className="relative p-1 rounded-2xl overflow-hidden">
                {/* Animated gradient border */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-primary-light/30 dark:from-primary/50 dark:to-primary-light/50 animate-pulse-soft rounded-2xl"></div>
                
                <div className="relative bg-white dark:bg-[#1E293B] rounded-xl p-6 md:p-8 shadow-xl">
                  <h3 className="text-2xl font-bold text-foreground dark:text-white mb-6">With CareAdvisor, you&apos;ll:</h3>
                  
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                        <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <span className="text-foreground dark:text-gray-200">Get immediate answers to complex Minnesota-specific compliance questions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                        <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <span className="text-foreground dark:text-gray-200">Eliminate hours spent searching through complex Minnesota regulations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                        <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <span className="text-foreground dark:text-gray-200">Receive guidance directly sourced from official government documents</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                        <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <span className="text-foreground dark:text-gray-200">Make confident compliance decisions backed by exact regulatory citations</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* CTA card */}
              <div className="relative">
                {/* Subtle glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/30 dark:to-primary-light/30 rounded-2xl blur-lg opacity-70"></div>
                
                <div className="relative flex items-center gap-6 bg-white dark:bg-[#1E293B] rounded-xl p-6 border border-gray-100 dark:border-gray-800/50 shadow-lg">
                  <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-primary dark:text-primary-light" />
                  </div>
                  <div>
                    <p className="text-lg text-foreground dark:text-white font-medium">
                      &ldquo;CareAdvisor saved me at least 10 hours a week on regulatory research.&rdquo;
                    </p>
                    <p className="text-sm text-muted-foreground dark:text-gray-400 mt-1">
                      — Minnesota Care Facility Director
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Benefits grid */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="group relative"
                  >
                    {/* Hover effect - glow */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/0 to-primary-light/0 group-hover:from-primary/20 group-hover:to-primary-light/20 dark:group-hover:from-primary/30 dark:group-hover:to-primary-light/30 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                    
                    <div className="bg-white dark:bg-[#1E293B] rounded-xl p-6 border border-gray-100 dark:border-gray-800/50 shadow-lg relative transition-all duration-300 h-full">
                      <div className="h-14 w-14 rounded-xl bg-primary/10 dark:bg-primary/20 mb-5 flex items-center justify-center text-primary dark:text-primary-light">
                        {benefit.icon}
                      </div>
                      
                      <h3 className="text-xl font-bold text-foreground dark:text-white mb-3">
                        {benefit.title}
                      </h3>
                      
                      <p className="text-muted-foreground dark:text-gray-300">
                        {benefit.description}
                      </p>

                      {/* Bottom accent line */}
                      <div className="h-1 w-10 bg-gradient-to-r from-primary to-primary-light rounded-full mt-5 transition-all duration-300 group-hover:w-16"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom call to action */}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 